import React from 'react';
import { Box, Card, CardContent, Grid, Skeleton } from '@mui/material';

interface SkeletonLoaderProps {
  variant: 'statCards' | 'form' | 'chart' | 'list';
}

const StatCardsSkeleton: React.FC = () => (
  <Grid container spacing={2}>
    {[1, 2, 3, 4].map((i) => (
      <Grid item xs={6} sm={6} lg={3} key={i}>
        <Card>
          <CardContent>
            <Skeleton variant="rounded" width={36} height={36} sx={{ mb: 1.5, borderRadius: '10px' }} />
            <Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="40%" height={28} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="80%" height={14} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const FormSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="30%" height={24} sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded" height={48} sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" height={48} sx={{ borderRadius: '10px' }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rounded" height={48} sx={{ flex: 1, borderRadius: '10px' }} />
          <Skeleton variant="rounded" height={48} sx={{ flex: 1, borderRadius: '10px' }} />
        </Box>
        <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: '10px', mt: 1 }} />
      </Box>
    </CardContent>
  </Card>
);

const ChartSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="25%" height={24} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={240} sx={{ borderRadius: '12px' }} />
    </CardContent>
  </Card>
);

const ListSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
      {[1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 1.5,
            borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none',
          }}
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={18} />
            <Skeleton variant="text" width="40%" height={14} />
          </Box>
          <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: '12px' }} />
        </Box>
      ))}
    </CardContent>
  </Card>
);

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ variant }) => {
  switch (variant) {
    case 'statCards':
      return <StatCardsSkeleton />;
    case 'form':
      return <FormSkeleton />;
    case 'chart':
      return <ChartSkeleton />;
    case 'list':
      return <ListSkeleton />;
  }
};
